import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/dashboard.css';
import logo from '../assets/logo.png';
import wave from '../assets/exam-hero-bg.png';
import cardPattern from '../assets/card-pattern.png';
import ProfileDropdown from '../components/ProfileDropdown';
import PortalFaqHelp from '../components/PortalFaqHelp';
import TestInstructions from '../components/TestInstructions';
import SetupExamModal, { COMPULSORY_SECTIONS, OPTIONAL_SECTIONS } from '../components/SetupExamModal';
import OtpModal from '../components/OtpModal';
import API from '../api/api';

const getSectionId = (section = {}, index = 0) => {
  if (section.id) return String(section.id);
  const baseName = section.title || section.name || `section-${index + 1}`;
  return String(baseName).trim().toLowerCase();
};

const normalizeSection = (section = {}, index = 0) => ({
  id: getSectionId(section, index),
  title: section.title || section.name || `Section ${index + 1}`,
  questions: Number(section.questions ?? section.numQuestions ?? 0),
  minutes: Number(section.minutes ?? section.timeMinutes ?? 0),
  isOptional: Boolean(section.isOptional ?? section.is_optional),
  isCompulsory: Boolean(section.isCompulsory ?? section.is_compulsory),
  description: section.description || `Assessment questions on ${section.title || section.name || `Section ${index + 1}`}`,
});

export default function Dashboard() {
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const storageUserId = storedUser.email || storedUser.id || 'guest';
  const [data, setData] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [driveStatus, setDriveStatus] = useState(null);
  const [isExamSetup, setIsExamSetup] = useState(false);
  const [selectedOptionalSections, setSelectedOptionalSections] = useState([]);
  const navigate = useNavigate();

  const examId = data?.exam?.id;
  const driveId = data?.exam?.drive_id || data?.exam?.driveId;
  const otpDriveId = driveStatus?.driveId || driveId;
  const shouldShowOtpModal = Boolean(
    (
      (driveStatus?.hasPendingDrive && driveStatus?.driveStatus === 'Added')
      || (data?.exam && driveId && String(data.exam.status || '').toLowerCase() === 'assigned')
    )
    && !otpVerified
  );
  const userSetupKey = examId ? `examSetupComplete:${storageUserId}:${examId}` : null;
  const selectedSectionsKey = examId ? `selectedOptionalSections:${storageUserId}:${examId}` : null;
  const examDisabledKey = examId && storageUserId ? `examDisabled_${storageUserId}_${examId}` : null;
  const examDisabled = data?.exam?.isExpired === true || (examDisabledKey ? localStorage.getItem(examDisabledKey) === 'true' : false);

  const handleSetupConfirm = (selectedOptional) => {
    if (!userSetupKey || !selectedSectionsKey || !examId) return;
    API.post(`/exam/${examId}/section-selection`, { selectedSectionIds: selectedOptional })
      .then(() => {
        localStorage.setItem(userSetupKey, 'true');
        localStorage.setItem(selectedSectionsKey, JSON.stringify(selectedOptional));
        localStorage.removeItem('examSetupComplete');
        localStorage.removeItem('selectedOptionalSections');
        setSelectedOptionalSections(selectedOptional);
        setIsExamSetup(true);
        setShowSetupModal(false);
      })
      .catch((err) => {
        console.error('Failed to save candidate section selection:', err);
        alert(err.response?.data?.message || 'Failed to save selected sections.');
      });
  };

  const examSections = (data?.exam?.sections || []).map((section, index) => normalizeSection(section, index));
  
  const compulsoryList = examSections.length > 0
    ? examSections.filter(s => !s.isOptional || s.isCompulsory)
    : COMPULSORY_SECTIONS;

  const optionalList = examSections.length > 0
    ? examSections.filter(s => s.isOptional && !s.isCompulsory)
    : OPTIONAL_SECTIONS;

  const selectedOptionalDetails = optionalList.filter(section => selectedOptionalSections.includes(section.id));
  const setupSections = [...compulsoryList, ...selectedOptionalDetails];
  const setupDuration = setupSections.reduce((total, section) => total + section.minutes, 0);
  const setupQuestions = setupSections.reduce((total, section) => total + section.questions, 0);
  const isExamAvailable = data?.exam?.isAvailable !== false;
  const submitReason = data?.exam?.result?.submitReason;

  const getCompletedMessage = () => {
    if (submitReason === 'tab_switch') {
      return [
        `Your exam was automatically submitted because tab switching was detected`,
        'Please contact the recruitment team if you believe this was a mistake.'
      ];
    }
    if (submitReason === 'screen_share_stopped') {
      return [
        'Your exam was automatically submitted because screen sharing was stopped during the assessment.',
        'Please contact the recruiting team if you need help.'
      ];
    }
    if (submitReason === 'timeout') {
      return [
        'Your exam was automatically submitted because the assessment time ended.',
        'Your responses have been saved for recruiter review.'
      ];
    }
    return [
      'You have successfully completed this assessment.',
      'Your response has been securely submitted, and your results will be reviewed by the recruiting team.'
    ];
  };

  const setupMessageLines = isExamAvailable
    ? [
        'The aptitude test is mandatory.',
        'You may select additional optional sections before proceeding.',
      ]
    : [
        `This exam will become available on ${data?.exam?.scheduledOn}.`,
        'You can start it only after that scheduled date and time.',
      ];

  // This dashboard bootstrap intentionally uses the values captured on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!userSetupKey || !selectedSectionsKey) {
      setIsExamSetup(false);
      setSelectedOptionalSections([]);
      return;
    }

    setIsExamSetup(localStorage.getItem(userSetupKey) === 'true');
    try {
      setSelectedOptionalSections(JSON.parse(localStorage.getItem(selectedSectionsKey) || '[]'));
    } catch {
      setSelectedOptionalSections([]);
    }
  }, [selectedSectionsKey, userSetupKey]);

  const fetchDashboardData = async () => {
      try {
        localStorage.removeItem('examDisabled');
        const [{ data: resData }, driveStatusRes] = await Promise.all([
          API.get('/exam/dashboard'),
          API.get('/drives/my-drive-status').catch(() => ({ data: null })),
        ]);
        setDriveStatus(driveStatusRes?.data || null);
        if (resData && resData.exam) {
          if (resData.exam.status?.toLowerCase() === 'results_published') {
            navigate('/result');
            return;
          }
          setData(resData);
          if (resData.exam.id) {
            localStorage.setItem('activeExamId', resData.exam.id);
            localStorage.setItem('activeExamTitle', resData.exam.title);
            if (resData.exam.closeAt) {
              localStorage.setItem(`examClosesAt_${resData.exam.id}`, resData.exam.closeAt);
            } else {
              localStorage.removeItem(`examClosesAt_${resData.exam.id}`);
            }
            if (resData.exam.openAt) {
              localStorage.setItem(`examOpensAt_${resData.exam.id}`, resData.exam.openAt);
            } else {
              localStorage.removeItem(`examOpensAt_${resData.exam.id}`);
            }
          }
          if (resData.exam.selectedOptionalSectionIds && userSetupKey && selectedSectionsKey) {
            const normalizedIds = resData.exam.selectedOptionalSectionIds.map((id) => String(id));
            localStorage.setItem(selectedSectionsKey, JSON.stringify(normalizedIds));
            localStorage.setItem(userSetupKey, 'true');
            setSelectedOptionalSections(normalizedIds);
            setIsExamSetup(true);
          }
          if (resData.exam.result) {
            localStorage.setItem('result', JSON.stringify(resData.exam.result));
          }
        } else {
          setData({
            user: storedUser.name || 'Candidate',
            exam: null,
            infoCards: [
              {
                title: 'System Readiness',
                items: ['Stable internet', 'Device should be charged', 'Use Chrome or Firefox browser'],
              },
              {
                title: 'Exam Guidelines',
                items: ['Do not refresh the screen', 'Complete exam in one go', 'Answers are auto saved'],
              },
              {
                title: 'Important Rules',
                items: ['Keep ID ready before the exam', 'Avoid switching tabs during exam', "Timer won't pause during the exam"],
              },
            ],
          });
        }
      } catch (err) {
        console.error('Failed to fetch candidate dashboard:', err);
        setDriveStatus(null);
        setData({
          user: storedUser.name || 'Candidate',
          exam: {
            status: 'Upcoming Exam',
            title: 'General Aptitude Test',
            scheduledOn: 'March 15, 2026',
          },
          infoCards: [
            {
              title: 'System Readiness',
              items: ['Stable internet', 'Device should be charged', 'Use Chrome or Firefox browser'],
            },
            {
              title: 'Exam Guidelines',
              items: ['Do not refresh the screen', 'Complete exam in one go', 'Answers are auto saved'],
            },
            {
              title: 'Important Rules',
              items: ['Keep ID ready before the exam', 'Avoid switching tabs during exam', "Timer won't pause during the exam"],
            },
          ],
        });
      }
    };

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (examId && setupDuration > 0) {
      localStorage.setItem(`examDuration_${examId}`, setupDuration);
    }
  }, [examId, setupDuration]);

  if (!data) {
    return <div className="dash-loading">Loading...</div>;
  }

  return (
    <div className="dash-page">
      <nav className="dash-nav">
        <img src={logo} alt="Inteliment" className="dash-logo" />

        <span className="dash-nav-title">Good To See You, {data.user} !</span>

        <div className="dash-nav-right">
          <PortalFaqHelp />
          <ProfileDropdown user={data.user} email={storedUser.email} />
        </div>
      </nav>

      <main className="dash-main">
        <section className="dash-hero">
          <div className="dash-card-shell">
            <div className="dash-card">
              <img src={wave} alt="" className="dash-card-bg" />

              <div className="dash-card-fade" />

              <div className="dash-card-inner">
                {data.exam ? (
                  data.exam.status?.toLowerCase() === 'completed' || data.exam.status?.toLowerCase() === 'results_published' ? (
                    <>
                      {data.exam.status?.toLowerCase() === 'results_published' ? (
                        <>
                          <span className="dash-badge" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>Results Published</span>

                          <h1 className="dash-exam-title">{data.exam.title}</h1>

                          <div className="dash-exam-meta">
                            <p style={{ color: '#4b5563', fontSize: 20, maxWidth: 590, lineHeight: 1.6, margin: 0 }}>
                              Your results have been evaluated and published! Click below to view your detailed performance report and score.
                            </p>
                            <button className="dash-btn" onClick={() => navigate('/result')} style={{ background: '#1D4ED8', color: '#FFFFFF', cursor: 'pointer' }}>
                              <span>View Result</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="dash-badge" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>Completed</span>

                          <h1 className="dash-exam-title">{data.exam.title}</h1>

                          <div className="dash-exam-meta">
                            <div className="dash-message-lines">
                              {getCompletedMessage().map((line, i) => (
                                <p key={i}>{line}</p>
                              ))}
                            </div>
                            <button className="dash-btn" disabled style={{ background: '#1D4ED8', color: '#FFFFFF', cursor: 'not-allowed', opacity: 0.9 }}>
                              <span>Exam Completed</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="dash-badge">{data.exam.status}</span>

                      <h1 className="dash-exam-title">{data.exam.title}</h1>

                      {isExamSetup ? (
                        <div className="dash-exam-meta">
                          <div className="dash-meta-group">
                            <div className="dash-meta-item">
                              <svg className="dash-meta-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              <div className="dash-meta-text">
                                <span className="dash-meta-label">Duration</span>
                                <span className="dash-meta-value">{setupDuration} minutes</span>
                              </div>
                            </div>

                            <div className="dash-meta-item">
                              <svg className="dash-meta-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                              </svg>
                              <div className="dash-meta-text">
                                <span className="dash-meta-label">Questions</span>
                                <span className="dash-meta-value">{setupQuestions} questions</span>
                              </div>
                            </div>

                            <div className="dash-meta-item">
                              <svg className="dash-meta-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                              <div className="dash-meta-text">
                                <span className="dash-meta-label">Scheduled on</span>
                                <span className="dash-meta-value">{data.exam.scheduledOn}</span>
                              </div>
                            </div>
                          </div>

                          {examDisabled ? (
                            <button className="dash-btn dash-btn--disabled" disabled>
                              <span>Exam Ended</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          ) : !isExamAvailable ? (
                            <button className="dash-btn dash-btn--disabled" disabled>
                              <span>Available on {data.exam.scheduledOn}</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          ) : (
                            <button className="dash-btn" onClick={() => setShowInstructions(true)}>
                              <span>Start Exam</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="dash-exam-meta">
                          <div className="dash-message-lines">
                            {setupMessageLines.map((line) => (
                              <p key={line}>{line}</p>
                            ))}
                          </div>
                          {isExamAvailable ? (
                            <button className="dash-btn" onClick={() => {
                              setShowSetupModal(true);
                            }}>
                              <span>Setup Exam</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          ) : (
                            <button className="dash-btn dash-btn--disabled" disabled>
                              <span>Available on {data.exam.scheduledOn}</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <>
                    <span className="dash-badge">No Exams Pending</span>
                    <h1 className="dash-exam-title">All Caught Up!</h1>
                    <div className="dash-exam-meta">
                      <p style={{ color: '#4b5563', fontSize: 20, maxWidth: 590, lineHeight: 1.6, margin: 0 }}>
                        You currently have no pending assessments assigned to you. You will be notified when a new exam is scheduled.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="dash-info-section">
          {data.infoCards.map((card, i) => (
            <div className="dash-info-card" key={i}>
              <div className="dash-info-card-overlay" />
              <div className="dash-info-card-content">
                <h3>{card.title}</h3>
                <ul>
                  {card.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
              <img src={cardPattern} alt="" className="dash-info-card-pattern" />
            </div>
          ))}
        </section>
      </main>

      {showSetupModal && (
        <SetupExamModal
          sections={data?.exam?.sections || []}
          minOptional={optionalList.length > 0 ? Math.max(1, Number(data?.exam?.min_optional ?? 1)) : 0}
          maxOptional={optionalList.length > 0 ? Math.max(1, Number(data?.exam?.max_optional ?? optionalList.length)) : 0}
          onClose={() => setShowSetupModal(false)}
          onConfirm={handleSetupConfirm}
        />
      )}

      {shouldShowOtpModal && otpDriveId && (
        <OtpModal
          driveId={otpDriveId}
          onVerified={() => {
            setOtpVerified(true);
            fetchDashboardData();
          }}
        />
      )}

      {showInstructions && (
        <TestInstructions
          onClose={() => setShowInstructions(false)}
          onStartExam={() => {
            setShowInstructions(false);
            navigate('/exam');
          }}
          questionCount={setupQuestions}
          durationMinutes={setupDuration}
        />
      )}
    </div>
  );
}
